/**
 * Custom hook for Venue Management
 */

import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  LIST_VENUES,
  SEARCH_VENUES,
  GET_VENUE,
  CREATE_VENUE,
  UPDATE_VENUE,
  DELETE_VENUE,
} from "../services/graphql/venue-operations";
import {
  Venue,
  VenueFilter,
  CreateVenueInput,
  UpdateVenueInput,
  VenueConnection,
} from "../types/asset-types";

export interface UseVenuesOptions {
  filter?: VenueFilter;
  limit?: number;
  autoFetch?: boolean;
}

export interface UseVenuesResult {
  // Data
  venues: Venue[];
  loading: boolean;
  error?: string;
  total: number;
  hasMore: boolean;

  // Actions
  refetch: () => void;
  loadMore: () => void;
  searchVenues: (query: string) => Promise<Venue[]>;
  createVenue: (input: CreateVenueInput) => Promise<Venue>;
  updateVenue: (input: UpdateVenueInput) => Promise<Venue>;
  deleteVenue: (id: string) => Promise<boolean>;

  // Filter management
  filter: VenueFilter;
  setFilter: (filter: VenueFilter) => void;
  clearFilter: () => void;
}

export function useVenues(options: UseVenuesOptions = {}): UseVenuesResult {
  const { filter: initialFilter = {}, limit = 20, autoFetch = true } = options;
  const [filter, setFilter] = useState<VenueFilter>(initialFilter);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [total, setTotal] = useState(0);
  const [nextToken, setNextToken] = useState<string | undefined>();

  // Query for listing venues
  const {
    data: listData,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
    fetchMore,
  } = useQuery(LIST_VENUES, {
    variables: { filter, limit },
    skip: !autoFetch,
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.listVenues) {
        setVenues(data.listVenues.items || []);
        setTotal(data.listVenues.total || 0);
        setNextToken(data.listVenues.nextToken);
      }
    },
    onError: (error) => {
      console.error("Error fetching venues:", error);
    },
  });

  // Search venues query - using useLazyQuery instead of useMutation
  const [searchVenuesQuery] = useLazyQuery(SEARCH_VENUES, {
    errorPolicy: "all",
  });

  // Mutations
  const [createVenueMutation] = useMutation(CREATE_VENUE, {
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.createVenue) {
        setVenues((prev) => [data.createVenue, ...prev]);
        setTotal((prev) => prev + 1);
      }
    },
  });

  const [updateVenueMutation] = useMutation(UPDATE_VENUE, {
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.updateVenue) {
        setVenues((prev) =>
          prev.map((venue) =>
            venue.id === data.updateVenue.id ? data.updateVenue : venue
          )
        );
      }
    },
  });

  const [deleteVenueMutation] = useMutation(DELETE_VENUE, {
    errorPolicy: "all",
    onCompleted: (data, { variables }) => {
      if (data?.deleteVenue && variables?.id) {
        setVenues((prev) => prev.filter((venue) => venue.id !== variables.id));
        setTotal((prev) => prev - 1);
      }
    },
  });

  // Actions
  const refetch = useCallback(() => {
    refetchList();
  }, [refetchList]);

  const loadMore = useCallback(async () => {
    if (!nextToken || listLoading) return;

    try {
      const result = await fetchMore({
        variables: {
          filter,
          limit,
          nextToken,
        },
      });

      if (result.data?.listVenues) {
        const newVenues = result.data.listVenues.items || [];
        setVenues((prev) => [...prev, ...newVenues]);
        setNextToken(result.data.listVenues.nextToken);
      }
    } catch (error) {
      console.error("Error loading more venues:", error);
    }
  }, [nextToken, listLoading, fetchMore, filter, limit]);

  const searchVenues = useCallback(
    async (query: string): Promise<Venue[]> => {
      try {
        const result = await searchVenuesQuery({
          variables: { query },
        });

        if (result.error) {
          throw new Error(result.error.message || "Failed to search venues");
        }

        return result.data?.searchVenues || [];
      } catch (error) {
        console.error("Error searching venues:", error);
        throw error;
      }
    },
    [searchVenuesQuery]
  );

  const createVenue = useCallback(
    async (input: CreateVenueInput): Promise<Venue> => {
      try {
        const result = await createVenueMutation({
          variables: { input },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to create venue"
          );
        }

        if (!result.data?.createVenue) {
          throw new Error("No venue data returned");
        }

        return result.data.createVenue;
      } catch (error) {
        console.error("Error creating venue:", error);
        throw error;
      }
    },
    [createVenueMutation]
  );

  const updateVenue = useCallback(
    async (input: UpdateVenueInput): Promise<Venue> => {
      try {
        const result = await updateVenueMutation({
          variables: { input },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to update venue"
          );
        }

        if (!result.data?.updateVenue) {
          throw new Error("No venue data returned");
        }

        return result.data.updateVenue;
      } catch (error) {
        console.error("Error updating venue:", error);
        throw error;
      }
    },
    [updateVenueMutation]
  );

  const deleteVenue = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await deleteVenueMutation({
          variables: { id },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to delete venue"
          );
        }

        return result.data?.deleteVenue || false;
      } catch (error) {
        console.error("Error deleting venue:", error);
        throw error;
      }
    },
    [deleteVenueMutation]
  );

  const clearFilter = useCallback(() => {
    setFilter({});
  }, []);

  // Update filter effect
  useEffect(() => {
    if (autoFetch) {
      refetch();
    }
  }, [filter, autoFetch, refetch]);

  return {
    // Data
    venues,
    loading: listLoading,
    error: listError?.message,
    total,
    hasMore: !!nextToken,

    // Actions
    refetch,
    loadMore,
    searchVenues,
    createVenue,
    updateVenue,
    deleteVenue,

    // Filter management
    filter,
    setFilter,
    clearFilter,
  };
}

// Hook for single venue
export function useVenue(id: string) {
  const { data, loading, error, refetch } = useQuery(GET_VENUE, {
    variables: { id },
    skip: !id,
    errorPolicy: "all",
  });

  return {
    venue: data?.getVenue,
    loading,
    error: error?.message,
    refetch,
  };
}
